import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';

interface SearchHit {
  readonly entityType: string;
  readonly id: string;
  readonly code: string;
  readonly title: string;
  readonly snippet: string;
  readonly category: string;
  readonly status: string;
}

interface SearchResult {
  readonly query: string;
  readonly total: number;
  readonly hits: SearchHit[];
}

const DESTINATIONS: readonly { id: string; to: string; label: string; description: string }[] = [
  { id: 'dashboard', to: '/admin/kms/dashboard', label: 'Knowledge Repository Dashboard', description: 'Corpus totals and governance health' },
  { id: 'articles', to: '/admin/kms/articles', label: 'Knowledge Article Editor', description: 'Author and govern knowledge articles' },
  { id: 'sops', to: '/admin/kms/sops', label: 'SOP Repository', description: 'Standard operating procedures' },
  { id: 'policies', to: '/admin/kms/policies', label: 'Policy Repository', description: 'Corporate policies' },
  { id: 'faqs', to: '/admin/kms/faqs', label: 'FAQ Management', description: 'Frequently asked questions' },
  { id: 'bestPractices', to: '/admin/kms/best-practices', label: 'Best Practice Library', description: 'Curated best practices' },
  { id: 'lessons', to: '/admin/kms/lessons', label: 'Lessons Learned', description: 'Project lessons learned' },
  { id: 'learning', to: '/admin/kms/learning', label: 'Learning Management', description: 'Courses, paths and assessments' },
  { id: 'courses', to: '/admin/kms/courses', label: 'Course Management', description: 'Learning course catalogue' },
  { id: 'training', to: '/admin/kms/training', label: 'Training Assignments', description: 'Assign and track training' },
  { id: 'certifications', to: '/admin/kms/certifications', label: 'Certifications', description: 'Issue and renew certifications' },
  { id: 'competencies', to: '/admin/kms/competencies', label: 'Competencies', description: 'Skill gap analysis' },
  { id: 'analytics', to: '/admin/kms/analytics', label: 'Knowledge Analytics', description: 'Metrics and summaries' },
];

/**
 * M35 — Knowledge Portal (§33). The landing hub for the Enterprise Knowledge
 * Management System: a §21 unified search box that queries across the entire
 * knowledge corpus (articles, SOPs, policies, FAQs, best practices, lessons,
 * courses) and a navigation grid linking to every KMS admin area. Tenant
 * isolation is enforced server-side (LAW 4).
 */
export function KnowledgePortal(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [result, setResult] = useState<SearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const search = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (!q.trim()) {
      setError(t('m35.portal.enterTerm'));
      return;
    }
    setBusy(true);
    try {
      const params = new URLSearchParams({ q: q.trim() });
      const r = await apiRequest<{ data: SearchResult }>(`/v1/kms/search?${params.toString()}`, { token });
      setResult(r.data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError(t('m35.portal.notAuthorised'));
      } else {
        setError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="knowledge-portal">
      <h1>{t('m35.portal.title')}</h1>

      <form aria-label="search-form" onSubmit={(e) => void search(e)}>
        <label>
          {t('m35.portal.searchLabel')}
          <input aria-label="field-query" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m35.portal.searchPh')} />
        </label>
        <Button variant="primary" type="submit" aria-label="submit-search" disabled={busy}>{busy ? t('m35.portal.searching') : t('m35.common.search')}</Button>
      </form>
      {error && <p role="alert" aria-label="search-error">{error}</p>}
      {result && (
        <section aria-label="search-results">
          <p aria-label="search-total">{t('m35.portal.total', { total: result.total, query: result.query })}</p>
          <ul aria-label="search-hits">
            {result.hits.map((h) => (
              <li key={`${h.entityType}-${h.id}`} data-type={h.entityType}>
                <span aria-label={`hit-title-${h.code}`}>{h.title}</span>
                <span aria-label={`hit-type-${h.code}`}>{h.entityType}</span>
                <span>{h.snippet}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <nav aria-label="kms-navigation" className="portal-grid">
        {DESTINATIONS.map((d) => (
          <Link key={d.to} to={d.to} aria-label={`nav-${d.label}`} className="portal-card">
            <span className="portal-card-title">{t(`m35.portal.nav.${d.id}.label`)}</span>
            <span className="portal-card-desc">{t(`m35.portal.nav.${d.id}.desc`)}</span>
          </Link>
        ))}
      </nav>
    </main>
  );
}
